import React, { useMemo } from 'react';
import type { Task } from '../types/task.type';

type QuadrantKey = 'DO_NOW' | 'SCHEDULE' | 'DELEGATE' | 'ELIMINATE';

const THRESHOLD = 3; // urgency/importance >= 3

function isValid15(n: number | undefined | null) {
    return typeof n === 'number' && n >= 1 && n <= 5;
}

function classify(u: number, i: number): QuadrantKey {
    const urgent = u >= THRESHOLD;
    const important = i >= THRESHOLD;

    if (important && urgent) return 'DO_NOW';
    if (important && !urgent) return 'SCHEDULE';
    if (!important && urgent) return 'DELEGATE';
    return 'ELIMINATE';
}

function titleOf(q: QuadrantKey) {
    switch (q) {
        case 'DO_NOW':
            return 'DO NOW';
        case 'SCHEDULE':
            return 'SCHEDULE';
        case 'DELEGATE':
            return 'DELEGATE';
        case 'ELIMINATE':
            return 'ELIMINATE';
    }
}

function descOf(q: QuadrantKey) {
    switch (q) {
        case 'DO_NOW':
            return 'Important & Urgent';
        case 'SCHEDULE':
            return 'Important & Not Urgent';
        case 'DELEGATE':
            return 'Not Important & Urgent';
        case 'ELIMINATE':
            return 'Not Important & Not Urgent';
    }
}

function boxClass(q: QuadrantKey) {
    switch (q) {
        case 'DO_NOW':
            return 'bg-rose-100 border-rose-200';
        case 'SCHEDULE':
            return 'bg-amber-100 border-amber-200';
        case 'DELEGATE':
            return 'bg-sky-100 border-sky-200';
        case 'ELIMINATE':
            return 'bg-emerald-100 border-emerald-200';
    }
}

export default function EisenhowerMatrix({ tasks }: { tasks: Task[] }) {
    const counts = useMemo(() => {
        const base: Record<QuadrantKey, number> = {
            DO_NOW: 0,
            SCHEDULE: 0,
            DELEGATE: 0,
            ELIMINATE: 0,
        };

        for (const t of tasks) {
            if (!isValid15(t.urgency) || !isValid15(t.importance)) continue;
            base[classify(t.urgency!, t.importance!)] += 1;
        }
        return base;
    }, [tasks]);

    const total =
        counts.DO_NOW +
        counts.SCHEDULE +
        counts.DELEGATE +
        counts.ELIMINATE;

    const Cell = ({ q }: { q: QuadrantKey }) => {
        const c = counts[q];
        const pct = total ? Math.round((c / total) * 100) : 0;

        return (
            <div
                className={`rounded-xl border px-3 py-2 h-[64px] ${boxClass(q)}`}
                title={`${titleOf(q)}: ${c} tasks (${pct}%)`}
            >
                {/* title row */}
                <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-gray-900">
                        {titleOf(q)}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 tabular-nums">
                        {c}
                    </div>
                </div>

                {/* desc row */}
                <div className="flex items-center justify-between mt-0.5">
                    <div className="text-[11px] text-gray-600">
                        {descOf(q)}
                    </div>
                    <div className="text-[11px] text-gray-600 tabular-nums">
                        {total ? `${pct}%` : ''}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full">
            {/* axis labels */}
            <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] text-gray-500">↑ Importance</div>
                <div className="text-[11px] text-gray-500">Urgency →</div>
            </div>

            {/* 2x2 grid
                위 = 중요 / 아래 = 비중요
                왼쪽 = 비긴급 / 오른쪽 = 긴급
            */}
            <div className="grid grid-cols-2 gap-3">
                {/* 중요 & 비긴급 */}
                <Cell q="SCHEDULE" />
                {/* 중요 & 긴급 */}
                <Cell q="DO_NOW" />
                {/* 비중요 & 비긴급 */}
                <Cell q="ELIMINATE" />
                {/* 비중요 & 긴급 */}
                <Cell q="DELEGATE" />
            </div>

            <div className="mt-2 text-[11px] text-gray-500">
                Eisenhower Matrix (urgency ≥ {THRESHOLD}, importance ≥ {THRESHOLD})
            </div>
        </div>
    );
}
